import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Button,
  Checkbox,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  OverlayDrawer,
  Spinner,
  Tooltip,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import {
  ALERT_TITLES,
  ALL_FEATURE_KEYS,
  AUDIENCE_OPTION_LABELS,
  AUDIENCE_OPTIONS,
  FEATURE_DISPLAY_NAMES,
  MESSAGE_KEY_DISPLAY_ORDER,
  MESSAGE_KEY_SEVERITIES,
  NEW_FEATURE_KEYS,
  SAMPLE_PARAMETERS,
  type AudienceOption,
} from '../constants/alerts';
import type { Alert, AlertMessageKey } from '../models/alert';
import { renderAlertMessage } from '../utils/alertMessages';
import { ApiService } from '../services/apiService';
import { getFriendlyApiErrorMessage, logApiError } from '../services/apiErrorMessages';
import { useAlertBannerFormFlyoutStyles } from '../styles/AlertBannerFormFlyout.styles';
import { AlertDatePicker } from './AlertDatePicker';
import { LocationIdsInput } from './LocationIdsInput';
import { MessageCardList, type MessageCardOption } from './MessageCardList';

interface AlertBannerFormFlyoutProps {
  open: boolean;
  onClose: () => void;
  /**
   * When present, opens the flyout in edit mode: header/button labels swap,
   * fields prefill from this alert, and Save issues PUT `/Alerts/{id}`
   * instead of POST. When null/undefined, the flyout is in add mode.
   */
  alertToEdit?: Alert | null;
  /** Invoked after a successful create or update so the parent can refresh its list. */
  onSaved?: () => void;
}

/** Formats a Date as DD/MM/YYYY (the format the API expects for maintenance parameters). */
const formatDateDMY = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
};

/**
 * Slide-in flyout for creating or updating an alert banner. Add vs edit mode
 * is driven by the presence of `alertToEdit`.
 */
export const AlertBannerFormFlyout: React.FC<AlertBannerFormFlyoutProps> = ({
  open,
  onClose,
  alertToEdit,
  onSaved,
}) => {
  const styles = useAlertBannerFormFlyoutStyles();
  const { instance, accounts } = useMsal();
  const today = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const initialEndDate = React.useCallback(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d;
  }, []);

  const [selectedTiers, setSelectedTiers] = useState<Set<AudienceOption>>(
    () => new Set<AudienceOption>(['All']),
  );
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [selectedMessageKey, setSelectedMessageKey] = useState<AlertMessageKey | null>(null);
  const [selectedFeatureKey, setSelectedFeatureKey] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(() => new Date());
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [isDismissible, setIsDismissible] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const endDateError = React.useMemo(() => {
    if (!startDate || !endDate) return null;
    return endDate.getTime() < startDate.getTime()
      ? 'End date must be on or after the start date.'
      : null;
  }, [startDate, endDate]);

  const longSpanWarning = React.useMemo(() => {
    if (!startDate || !endDate) return null;
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const spanDays = (endDate.getTime() - startDate.getTime()) / MS_PER_DAY;
    return spanDays > 14
      ? "Heads up: You've set this alert to remain active for more than two weeks. Double check that this is what you intended."
      : null;
  }, [startDate, endDate]);

  const isEditMode = alertToEdit != null;

  // Reset (add mode) or prefill (edit mode) whenever the flyout opens.
  React.useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    if (alertToEdit) {
      setSelectedTiers(
        alertToEdit.targetTiers == null
          ? new Set<AudienceOption>(['All'])
          : alertToEdit.targetTiers.length === 0
            ? new Set<AudienceOption>(['None'])
            : new Set<AudienceOption>(alertToEdit.targetTiers as AudienceOption[]),
      );
      setLocationIds(alertToEdit.targetLocationIds ?? []);
      setSelectedMessageKey(alertToEdit.messageKey);
      setSelectedFeatureKey(alertToEdit.parameters?.featureKey ?? null);
      setStartDate(new Date(alertToEdit.startTimeUtc));
      setEndDate(new Date(alertToEdit.endTimeUtc));
      setIsDismissible(alertToEdit.isDismissible);
    } else {
      setSelectedTiers(new Set<AudienceOption>(['All']));
      setLocationIds([]);
      setSelectedMessageKey(null);
      setSelectedFeatureKey(null);
      setStartDate(new Date());
      setEndDate(initialEndDate());
      setIsDismissible(true);
    }
    // Only re-run on open transitions or when the target alert changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, alertToEdit]);

  const featureKeysForSelection = React.useMemo<readonly string[] | null>(() => {
    if (selectedMessageKey === 'NewFeatureAvailable') return NEW_FEATURE_KEYS;
    if (selectedMessageKey === 'FeatureTemporarilyUnavailable') return ALL_FEATURE_KEYS;
    return null;
  }, [selectedMessageKey]);

  const handleSelectMessage = React.useCallback((key: AlertMessageKey) => {
    setSelectedMessageKey(key);
    // Reset the feature selection when the message changes, since the eligible
    // set differs between NewFeatureAvailable and FeatureTemporarilyUnavailable.
    setSelectedFeatureKey(null);
  }, []);

  const messageOptions = React.useMemo<MessageCardOption[]>(
    () =>
      MESSAGE_KEY_DISPLAY_ORDER.map((key) => {
        // Synthesize a minimal Alert so we can reuse the same template
        // expansion the runtime uses; only messageKey + parameters matter here.
        const sampleAlert = {
          messageKey: key,
          parameters: SAMPLE_PARAMETERS[key] ?? null,
        } as Alert;
        return {
          key,
          title: ALERT_TITLES[key],
          body: renderAlertMessage(sampleAlert),
          severity: MESSAGE_KEY_SEVERITIES[key],
        };
      }),
    [],
  );

  const toggleTier = (option: AudienceOption, checked: boolean) => {
    setSelectedTiers((prev) => {
      if (option === 'All' || option === 'None') {
        // Clicking `All` or `None`: force that option exclusively. Ignore
        // unchecking, since the tier group should never be empty — use the
        // `None` checkbox to explicitly target zero tiers.
        return checked ? new Set<AudienceOption>([option]) : prev;
      }

      const next = new Set(prev);
      if (checked) {
        next.add(option);
        // Picking a specific tier turns off the `All` / `None` shortcuts.
        next.delete('All');
        next.delete('None');
      } else {
        next.delete(option);
        // If no specific tiers remain, snap to `None` so the intent
        // (target only Location IDs, if any) is explicit.
        if (next.size === 0) next.add('None');
      }
      return next;
    });
  };

  const resetForm = React.useCallback(() => {
    setSelectedTiers(new Set<AudienceOption>(['All']));
    setLocationIds([]);
    setSelectedMessageKey(null);
    setSelectedFeatureKey(null);
    setStartDate(new Date());
    setEndDate(initialEndDate());
    setIsDismissible(true);
    setSubmitError(null);
  }, [initialEndDate]);

  const handleCancel = React.useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const featureRequired =
    selectedMessageKey === 'NewFeatureAvailable' ||
    selectedMessageKey === 'FeatureTemporarilyUnavailable';

  // Alert must reach *someone*: either a tier selection other than `None`,
  // or at least one Location ID.
  const hasTierAudience =
    selectedTiers.size > 0 && !(selectedTiers.size === 1 && selectedTiers.has('None'));
  const audienceError =
    !hasTierAudience && locationIds.length === 0
      ? 'Select at least one tier or add a Location ID.'
      : null;

  const canSubmit =
    !isSubmitting &&
    selectedMessageKey !== null &&
    (!featureRequired || selectedFeatureKey !== null) &&
    startDate !== null &&
    endDate !== null &&
    endDate.getTime() >= startDate.getTime() &&
    audienceError === null;

  const handleSave = async () => {
    if (!canSubmit || !selectedMessageKey || !startDate || !endDate) return;

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      setSubmitError('No authenticated account found.');
      return;
    }

    // Map tier selection to the wire format:
    //  - `All`  → null  (unrestricted; every tier matches)
    //  - `None` → []    (explicit: no tiers — reach only Location IDs)
    //  - specific tiers → those tiers verbatim
    let targetTiers: string[] | null;
    if (selectedTiers.has('All')) {
      targetTiers = null;
    } else if (selectedTiers.has('None')) {
      targetTiers = [];
    } else {
      targetTiers = [...selectedTiers] as string[];
    }

    let parameters: Record<string, string> | null = null;
    if (featureRequired && selectedFeatureKey) {
      parameters = { featureKey: selectedFeatureKey };
    } else if (selectedMessageKey === 'ScheduledMaintenance') {
      // Reuse the alert's start/end for the template placeholders. API expects
      // DD/MM/YYYY per Project Plumeria / Alerts.md.
      parameters = {
        maintenanceStart: formatDateDMY(startDate),
        maintenanceEnd: formatDateDMY(endDate),
      };
    }

    const payload = {
      messageKey: selectedMessageKey,
      parameters,
      severity: MESSAGE_KEY_SEVERITIES[selectedMessageKey],
      isDismissible,
      startTimeUtc: startDate.toISOString(),
      endTimeUtc: endDate.toISOString(),
      targetTiers,
      targetLocationIds: locationIds.length > 0 ? locationIds : null,
    };

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const api = new ApiService(instance, account);
      if (isEditMode && alertToEdit) {
        await api.put<void>(`/Alerts/${encodeURIComponent(alertToEdit.id)}`, payload);
      } else {
        await api.post<{ id: string }>('/Alerts', payload);
      }
      onSaved?.();
      resetForm();
      onClose();
    } catch (err) {
      logApiError(
        isEditMode ? 'AlertBannerFormFlyout.handleSave' : 'AlertBannerFormFlyout.handleAdd',
        err,
      );
      setSubmitError(
        getFriendlyApiErrorMessage(
          err,
          isEditMode ? 'Failed to update the alert.' : 'Failed to create the alert.',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OverlayDrawer
      open={open}
      onOpenChange={(_e, { open: isOpen }) => {
        if (!isOpen && !isSubmitting) handleCancel();
      }}
      position="end"
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Tooltip content="Close" relationship="label">
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<DismissRegular />}
                onClick={handleCancel}
                disabled={isSubmitting}
              />
            </Tooltip>
          }
        >
          {isEditMode ? 'Update banner' : 'Add banner'}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.body}>
          {submitError && (
            <MessageBar intent="error">
              <MessageBarBody>{submitError}</MessageBarBody>
            </MessageBar>
          )}

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} id="audience-label">
              Audience
              <span className={styles.requiredMarker} aria-hidden="true">*</span>
            </label>
            {audienceError && (
              <span className={styles.audienceError} role="alert">
                {audienceError}
              </span>
            )}
            <div
              className={styles.nestedField}
              role="group"
              aria-labelledby="audience-label tier-label"
            >
              <div className={styles.angleIndicator} aria-hidden="true" />
              <div className={styles.nestedFieldContent}>
                <span className={styles.nestedFieldLabel} id="tier-label">
                  Tier
                </span>
                <div className={styles.checkboxRow}>
                  {AUDIENCE_OPTIONS.map((option) => (
                    <Checkbox
                      key={option}
                      indicator={{ className: styles.tierCheckboxIndicator }}
                      label={AUDIENCE_OPTION_LABELS[option]}
                      checked={selectedTiers.has(option)}
                      onChange={(_e, data) => toggleTier(option, !!data.checked)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div
              className={styles.nestedField}
              role="group"
              aria-labelledby="audience-label location-ids-label"
            >
              <div className={styles.angleIndicator} aria-hidden="true" />
              <div className={styles.nestedFieldContent}>
                <label
                  className={styles.nestedFieldLabel}
                  htmlFor="location-ids-input"
                  id="location-ids-label"
                >
                  Location IDs
                </label>
                <span className={styles.hint}>
                  Selecting tiers targets all users in those tiers; adding Location IDs
                  extends the alert to users in those locations as well.
                </span>
                <LocationIdsInput
                  value={locationIds}
                  onChange={setLocationIds}
                  inputId="location-ids-input"
                  ariaLabelledBy="location-ids-label"
                />
              </div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} id="message-label">
              Message
              <span className={styles.requiredMarker} aria-hidden="true">*</span>
            </label>
            <MessageCardList
              options={messageOptions}
              selectedKey={selectedMessageKey}
              onSelect={handleSelectMessage}
              readOnly={isEditMode}
              ariaLabelledBy="message-label"
            />
          </div>

          {featureKeysForSelection && (
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="feature-dropdown" id="feature-label">
                Feature
                <span className={styles.requiredMarker} aria-hidden="true">*</span>
              </label>
              <Dropdown
                id="feature-dropdown"
                placeholder="Select feature"
                className={styles.featureDropdown}
                value={
                  selectedFeatureKey
                    ? FEATURE_DISPLAY_NAMES[selectedFeatureKey] ?? selectedFeatureKey
                    : ''
                }
                selectedOptions={selectedFeatureKey ? [selectedFeatureKey] : []}
                onOptionSelect={(_e, data) => setSelectedFeatureKey(data.optionValue ?? null)}
              >
                {featureKeysForSelection.map((key) => (
                  <Option key={key} value={key} text={FEATURE_DISPLAY_NAMES[key] ?? key}>
                    {FEATURE_DISPLAY_NAMES[key] ?? key}
                  </Option>
                ))}
              </Dropdown>
            </div>
          )}

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="start-date-input">
              Start date
              <span className={styles.requiredMarker} aria-hidden="true">*</span>
            </label>
            <AlertDatePicker
              id="start-date-input"
              className={styles.dateInput}
              value={startDate}
              onChange={setStartDate}
              minDate={isEditMode ? undefined : today}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="end-date-input">
              End date
              <span className={styles.requiredMarker} aria-hidden="true">*</span>
            </label>
            <Field
              validationState={endDateError ? 'error' : undefined}
              validationMessage={endDateError ?? undefined}
            >
              <AlertDatePicker
                id="end-date-input"
                className={styles.dateInput}
                value={endDate}
                onChange={setEndDate}
                minDate={startDate ?? undefined}
              />
            </Field>
            {longSpanWarning && (
              <MessageBar intent="warning" className={styles.dateSpanWarning}>
                <MessageBarBody>{longSpanWarning}</MessageBarBody>
              </MessageBar>
            )}
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button
          appearance="primary"
          onClick={() => void handleSave()}
          disabled={!canSubmit}
          icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
        >
          {isSubmitting
            ? isEditMode ? 'Saving…' : 'Adding…'
            : isEditMode ? 'Save' : 'Add'}
        </Button>
        <Button appearance="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};
